import { useState, useEffect, useRef } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import { Camera, CheckCircle2, XCircle, Shield, ArrowLeft, AlertTriangle, MapPin, Clock, ChevronRight } from 'lucide-react'
import { verifyService, VerifyResult } from '../services/verifyService'
import { useToast } from '../components/Toast'

type LocalVerifyResult = VerifyResult & {
  firstScan: boolean
  scanCount: number
  lastScanAt?: string
  asset?: {
    name: string
    brand: string
    image?: string
  }
}

export default function Verify() {
  const [scanning, setScanning] = useState(false)
  const [result, setResult] = useState<LocalVerifyResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [destroyOpen, setDestroyOpen] = useState(false)
  const [destroyCode, setDestroyCode] = useState('')
  const [destroyType, setDestroyType] = useState<'tampered' | 'peeled' | 'damaged'>('damaged')
  const [destroying, setDestroying] = useState(false)
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const toast = useToast()

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {})
      }
    }
  }, [])

  const startScan = async () => {
    setScanning(true)
    setResult(null)
    const scanner = new Html5Qrcode('qr-reader')
    scannerRef.current = scanner

    try {
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          handleScan(decodedText)
          scanner.stop().catch(() => {})
          setScanning(false)
        },
        () => {}
      )
    } catch (err) {
      console.error('Camera error:', err)
      toast.error('无法访问摄像头')
      setScanning(false)
    }
  }

    const handleDestroyReport = async () => {
    if (!destroyCode.trim()) {
      toast.warning('请输入或扫描要作废的防伪码')
      return
    }
    setDestroying(true)
    try {
      await verifyService.destroyReport({
        code: destroyCode.trim(),
        destroyType,
      } as any)
      toast.success('标签作废已记录，该码后续验真将提示已作废')
      setDestroyOpen(false)
      setDestroyCode('')
    } catch (e) {
      toast.error((e as Error).message || '上报失败')
    } finally {
      setDestroying(false)
    }
  }

const handleScan = async (code: string) => {
    setLoading(true)
    try {
      const result = await verifyService.verifyByCode({ code })
      setResult(result as LocalVerifyResult)
    } catch (error: any) {
      toast.error(error.message || '验证失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  const reset = () => {
    setResult(null)
    setScanning(false)
  }

  const getRiskLevel = (score: number) => {
    if (score >= 70) return { label: '高风险', color: 'text-cyber-red', bg: 'bg-cyber-red/20' }
    if (score >= 40) return { label: '中风险', color: 'text-cyber-yellow', bg: 'bg-cyber-yellow/20' }
    return { label: '低风险', color: 'text-cyber-green', bg: 'bg-cyber-green/20' }
  }

  return (
    <div className="space-y-6 py-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-cyber-neon" />
        <h2 className="font-cyber text-xl font-bold neon-text">防伪验真</h2>
      </div>

      {/* Scanner Area */}
      <div className="cyber-card rounded-2xl p-1 corner-accent">
        <div className="relative aspect-square bg-cyber-dark rounded-xl overflow-hidden">
          {scanning ? (
            <div id="qr-reader" className="w-full h-full" />
          ) : result ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 overflow-y-auto">
              {result.isAuthentic ? (
                <>
                  <CheckCircle2 className="w-16 h-16 text-cyber-green mb-4 shrink-0" />
                  <div className="text-cyber-green font-cyber text-lg font-bold mb-2">验证通过</div>
                  <div className="text-center space-y-1">
                    <div className="text-white font-medium">{result.asset?.name || '未知商品'}</div>
                    <div className="text-gray-400 text-sm">{result.asset?.brand || '—'}</div>
                  </div>

                  {/* 首次扫描标记 */}
                  {result.firstScan && (
                    <div className="mt-3 px-3 py-1.5 bg-cyber-green/20 border border-cyber-green/30 rounded-lg">
                      <span className="text-cyber-green text-xs font-medium">✓ 首次验证</span>
                    </div>
                  )}

                  {/* 风险评分 */}
                  <div className="mt-4 w-full max-w-xs">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-400">风险评分</span>
                      <span className={`text-xs font-bold ${getRiskLevel(result.riskScore).color}`}>
                        {result.riskScore}/100 ({getRiskLevel(result.riskScore).label})
                      </span>
                    </div>
                    <div className="h-2 bg-cyber-dark rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          result.riskScore >= 70 ? 'bg-cyber-red' :
                          result.riskScore >= 40 ? 'bg-cyber-yellow' : 'bg-cyber-green'
                        }`}
                        style={{ width: `${result.riskScore}%` }}
                      />
                    </div>
                  </div>

                  {/* 扫描记录 */}
                  <div className="mt-4 w-full max-w-xs space-y-2">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      <span>已扫描 {result.scanCount} 次</span>
                    </div>
                    {result.lastScanAt && (
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <MapPin className="w-3 h-3" />
                        <span>上次扫描: {new Date(result.lastScanAt).toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  {/* 警告信息 */}
                  {result.warnings.length > 0 && (
                    <div className="mt-4 w-full max-w-xs p-3 bg-cyber-yellow/20 border border-cyber-yellow/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-cyber-yellow" />
                        <span className="text-cyber-yellow text-xs font-bold">安全提示</span>
                      </div>
                      <ul className="text-xs text-cyber-yellow/80 space-y-1">
                        {result.warnings.map((w, i) => (
                          <li key={i}>• {w}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={reset}
                    className="mt-6 cyber-btn px-6 py-2 rounded-lg text-sm"
                  >
                    <ArrowLeft className="w-4 h-4 inline mr-1" />
                    继续扫描
                  </button>
                </>
              ) : (
                <>
                  <XCircle className="w-16 h-16 text-cyber-red mb-4" />
                  <div className="text-cyber-red font-cyber text-lg font-bold mb-2">验证失败</div>
                  <div className="text-gray-400 text-sm text-center mb-4">该商品未通过防伪验证，可能存在风险</div>

                  {/* 风险评分 */}
                  <div className="mt-2 w-full max-w-xs">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-400">风险评分</span>
                      <span className={`text-xs font-bold ${getRiskLevel(result.riskScore).color}`}>
                        {result.riskScore}/100 ({getRiskLevel(result.riskScore).label})
                      </span>
                    </div>
                    <div className="h-2 bg-cyber-dark rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-cyber-red"
                        style={{ width: `${result.riskScore}%` }}
                      />
                    </div>
                  </div>

                  <button
                    onClick={reset}
                    className="mt-6 cyber-btn px-6 py-2 rounded-lg text-sm border-cyber-red text-cyber-red hover:bg-cyber-red/10"
                  >
                    <ArrowLeft className="w-4 h-4 inline mr-1" />
                    重新扫描
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
              <Camera className="w-16 h-16 text-cyber-neon mb-4 opacity-50" />
              <div className="text-gray-400 text-sm text-center mb-6">
                对准商品上的防伪二维码或 NFC 芯片进行扫描
              </div>
              <button
                onClick={startScan}
                disabled={loading}
                className="cyber-btn px-8 py-3 rounded-lg text-sm font-medium disabled:opacity-50"
              >
                <Camera className="w-4 h-4 inline mr-2" />
                {loading ? '验证中...' : '开始扫描'}
              </button>
            </div>
          )}

          {/* Scan frame overlay */}
          {!result && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 border border-cyber-neon/30 rounded-lg">
                <div className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 border-cyber-neon" />
                <div className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2 border-cyber-neon" />
                <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2 border-cyber-neon" />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 border-cyber-neon" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="cyber-card rounded-xl p-4 space-y-3">
        <div className="text-sm font-medium text-gray-300">使用说明</div>
        <div className="space-y-2 text-xs text-gray-500">
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-cyber-neon/20 border border-cyber-neon/50 flex items-center justify-center text-cyber-neon text-[10px] shrink-0">1</div>
            <span>找到商品上的防伪二维码或 NFC 芯片</span>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-cyber-neon/20 border border-cyber-neon/50 flex items-center justify-center text-cyber-neon text-[10px] shrink-0">2</div>
            <span>点击「开始扫描」，对准代码区域</span>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-cyber-neon/20 border border-cyber-neon/50 flex items-center justify-center text-cyber-neon text-[10px] shrink-0">3</div>
            <span>系统自动验证动态签名，显示验真结果与风险提示</span>
          </div>
        </div>
      </div>

      {/* 标签销毁上报（防转移）*/}
      <button onClick={() => setDestroyOpen(true)}
        className="w-full cyber-card rounded-xl p-3 text-left flex items-center justify-between hover:border-cyber-yellow/50 transition-colors">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-cyber-yellow" />
          <span className="text-xs text-gray-300">标签破损 / 被撕下？点击作废防伪码</span>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-600" />
      </button>

      {destroyOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-end z-50" onClick={() => setDestroyOpen(false)}>
          <div className="w-full bg-cyber-dark border-t border-cyber-border rounded-t-2xl p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}>
            <div className="text-white font-bold">作废防伪标签</div>
            <p className="text-xs text-gray-500">作废后该码将标记 REVOKED，他人扫描会收到"标签已作废"提示，防止标签被转移到假货上。</p>
            <input
              className="w-full px-3 py-3 bg-cyber-panel border border-cyber-border rounded-xl text-white text-sm"
              placeholder="输入或粘贴防伪码"
              value={destroyCode}
              onChange={(e) => setDestroyCode(e.target.value)}
            />
            <div className="flex gap-2">
              {([['tampered', '被篡改'], ['peeled', '被撕下'], ['damaged', '自然损坏']] as const).map(([v, l]) => (
                <button key={v} onClick={() => setDestroyType(v)}
                  className={`flex-1 py-2 rounded-lg text-xs border transition-colors ${
                    destroyType === v ? 'border-cyber-yellow text-cyber-yellow bg-cyber-yellow/10' : 'border-cyber-border text-gray-500'
                  }`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={handleDestroyReport} disabled={destroying}
                className="flex-1 cyber-btn py-3 rounded-xl text-sm disabled:opacity-50">
                {destroying ? '提交中...' : '确认作废'}
              </button>
              <button onClick={() => setDestroyOpen(false)}
                className="px-5 py-3 rounded-xl border border-cyber-border text-gray-400 text-sm">取消</button>
            </div>
          </div>
        </div>
      )}

      {/* Features */}
      <div className="grid grid-cols-2 gap-3">
        <div className="cyber-card rounded-xl p-3 text-center">
          <Shield className="w-5 h-5 text-cyber-neon mx-auto mb-1" />
          <div className="text-xs text-gray-400">动态签名</div>
          <div className="text-xs text-cyber-neon font-bold">HMAC-SHA256</div>
        </div>
        <div className="cyber-card rounded-xl p-3 text-center">
          <AlertTriangle className="w-5 h-5 text-cyber-yellow mx-auto mb-1" />
          <div className="text-xs text-gray-400">风控预警</div>
          <div className="text-xs text-cyber-yellow font-bold">实时检测</div>
        </div>
      </div>
    </div>
  )
}
